import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createIndividual } from '../services/individualsService';
import { getFDClientForIntake, linkFDClient } from '../services/foodDistributionService';
import {
  checkIntakeDuplicates,
  IntakeSuggestion,
} from '../services/duplicateService';
import { useServiceAreaZips } from '../hooks/useServiceAreaZips';
import '../styles/FormPages.css';

interface ClientIntakeForm {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  raceEthnicity: string;
  address: string;
  city: string;
  zipCode: string;
  phoneNumber: string;
  emailAddress: string;
  noPhoneAvailable: boolean;
  noEmailAvailable: boolean;
  // Child-specific fields
  isChild: boolean;
  schoolName?: string;
  grade?: string;
  hasAttendanceConcerns?: boolean;
  hasDisability?: boolean;
}

export function ClientIntakePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fdClientId = searchParams.get('fdClientId');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preFilled, setPreFilled] = useState(false);
  const { isInServiceArea: checkServiceArea } = useServiceAreaZips();
  const [formData, setFormData] = useState<ClientIntakeForm>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    raceEthnicity: '',
    address: '',
    city: 'Miami',
    zipCode: '',
    phoneNumber: '',
    emailAddress: '',
    noPhoneAvailable: false,
    noEmailAvailable: false,
    isChild: false,
  });

  // Pre-fill from FD client data when fdClientId is in the URL
  useEffect(() => {
    if (!fdClientId) {
      return;
    }
    getFDClientForIntake(parseInt(fdClientId, 10))
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          firstName: data.firstName || prev.firstName,
          lastName: data.lastName || prev.lastName,
          phoneNumber: data.phoneNumber || prev.phoneNumber,
          zipCode: data.zipCode || prev.zipCode,
          city: data.city || prev.city,
        }));
        setPreFilled(true);
      })
      .catch((err) => {
        console.warn('Could not pre-fill from FD client:', err);
      });
  }, [fdClientId]);

  const calculateAge = (dob: string): number => {
    if (!dob) {
      return 0;
    }
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // ---- Duplicate auto-suggest ----
  const [duplicates, setDuplicates] = useState<IntakeSuggestion[]>([]);
  const [duplicatesDismissed, setDuplicatesDismissed] = useState(false);

  useEffect(() => {
    if (duplicatesDismissed) {return;}
    const first = formData.firstName.trim();
    const last = formData.lastName.trim();
    if (!first || !last) {
      setDuplicates([]);
      return;
    }
    const handle = setTimeout(() => {
      checkIntakeDuplicates({
        firstName: first,
        lastName: last,
        dateOfBirth: formData.dateOfBirth || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        emailAddress: formData.emailAddress || undefined,
      })
        .then(setDuplicates)
        .catch(() => setDuplicates([]));
    }, 500);
    return () => clearTimeout(handle);
  }, [
    formData.firstName,
    formData.lastName,
    formData.dateOfBirth,
    formData.phoneNumber,
    formData.emailAddress,
    duplicatesDismissed,
  ]);

  const isInServiceArea = checkServiceArea(formData.zipCode);
  const age = calculateAge(formData.dateOfBirth);
  // Show child fields for all minors (0-17), but only when DOB is entered
  const showChildFields = formData.dateOfBirth && age >= 0 && age < 18;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare request body - use undefined for phone/email when marked as unavailable
      const { noPhoneAvailable, noEmailAvailable, ...restFormData } = formData;
      const requestBody = {
        ...restFormData,
        phoneNumber: noPhoneAvailable ? undefined : formData.phoneNumber || undefined,
        emailAddress: noEmailAvailable ? undefined : formData.emailAddress || undefined,
        intakeDate: new Date().toISOString(),
        clientType: 'Individual' as const,
      };

      const newIndividual = await createIndividual(requestBody);

      // Auto-link FD client to the new Individual if we came from FD pre-fill
      if (fdClientId && newIndividual?.individualID) {
        try {
          await linkFDClient(parseInt(fdClientId, 10), newIndividual.individualID);
        } catch {
          // FD link can be done manually if auto-link fails
        }
      }

      navigate('/clients');
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      console.error('=== Error Creating Client ===');
      console.error('Full Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="page-header">
        <h2>New Client Intake</h2>
        <p>Complete this form to add a new client to the system</p>
      </div>

      {preFilled && (
        <div className="alert alert-info">
          <strong>Pre-filled from Food Distribution record.</strong> Please verify and complete
          missing fields.
        </div>
      )}

      {isInServiceArea && (
        <div className="alert alert-info">
          <strong>Service Area Client:</strong> This client is in a WeCare Grant service area zip
          code ({formData.zipCode}). CANS assessments will be required every 60 days.
        </div>
      )}

      {duplicates.length > 0 && !duplicatesDismissed && (
        <div className="alert" style={{ background: '#fff8e6', border: '1px solid #FAA31B' }}>
          <strong>Possible existing client{duplicates.length > 1 ? 's' : ''} found.</strong>{' '}
          Before creating a new record, check whether this is one of the following:
          <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            {duplicates.map((s) => (
              <li key={s.individualID}>
                <Link to={`/clients/${s.individualID}`}>
                  {s.clientID || `#${s.individualID}`} — {s.firstName} {s.lastName}
                </Link>
                {s.dateOfBirth && ` · DOB ${s.dateOfBirth.slice(0, 10)}`}
                {' · '}
                <em>{s.reasons.join('; ')}</em>{' '}
                <span style={{ color: '#666' }}>({s.confidence}% match)</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="button-secondary"
            onClick={() => setDuplicatesDismissed(true)}
          >
            Not a duplicate, continue
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="intake-form">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-section">
          <h3>Personal Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth *</label>
              <input
                type="date"
                id="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                required
              />
              {age > 0 && <small className="form-hint">Age: {age} years</small>}
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender *</label>
              <select
                id="gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                required
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="raceEthnicity">Race/Ethnicity *</label>
            <select
              id="raceEthnicity"
              value={formData.raceEthnicity}
              onChange={(e) => setFormData({ ...formData, raceEthnicity: e.target.value })}
              required
            >
              <option value="">Select...</option>
              <option value="Hispanic or Latino">Hispanic or Latino</option>
              <option value="Black or African American">Black or African American</option>
              <option value="White">White</option>
              <option value="Asian">Asian</option>
              <option value="Native Hawaiian or Pacific Islander">
                Native Hawaiian or Pacific Islander
              </option>
              <option value="American Indian or Alaska Native">
                American Indian or Alaska Native
              </option>
              <option value="Two or more races">Two or more races</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Contact Information</h3>

          <div className="form-group">
            <label htmlFor="address">Street Address *</label>
            <input
              type="text"
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="zipCode">Zip Code *</label>
              <input
                type="text"
                id="zipCode"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                pattern="[0-9]{5}"
                maxLength={5}
                required
              />
              {isInServiceArea && (
                <small className="form-hint success">✓ Service Area Zip Code</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phoneNumber">
                Phone Number {showChildFields || formData.noPhoneAvailable ? '' : '*'}
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                pattern="[0-9]{10}"
                placeholder="3051234567"
                required={!showChildFields && !formData.noPhoneAvailable}
                disabled={formData.noPhoneAvailable}
                style={
                  formData.noPhoneAvailable ? { backgroundColor: '#f5f5f5', color: '#999' } : {}
                }
              />
              <label
                className="checkbox-label"
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.noPhoneAvailable}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      noPhoneAvailable: e.target.checked,
                      phoneNumber: e.target.checked ? '' : formData.phoneNumber,
                    })
                  }
                />
                No phone number available
              </label>
              {showChildFields && !formData.noPhoneAvailable && (
                <small className="form-hint">Optional for children/teenagers</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="emailAddress">
                Email Address {formData.noEmailAvailable ? '' : ''}
              </label>
              <input
                type="email"
                id="emailAddress"
                value={formData.emailAddress}
                onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                disabled={formData.noEmailAvailable}
                style={
                  formData.noEmailAvailable ? { backgroundColor: '#f5f5f5', color: '#999' } : {}
                }
              />
              <label
                className="checkbox-label"
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.noEmailAvailable}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      noEmailAvailable: e.target.checked,
                      emailAddress: e.target.checked ? '' : formData.emailAddress,
                    })
                  }
                />
                No email address available
              </label>
            </div>
          </div>
        </div>

        {showChildFields && (
          <div className="form-section">
            <h3>Child Information</h3>
            <p className="form-section-description">Additional information for clients under 18</p>

            <div className="form-group">
              <label htmlFor="schoolName">School Name</label>
              <input
                type="text"
                id="schoolName"
                value={formData.schoolName || ''}
                onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="grade">Grade Level</label>
                <select
                  id="grade"
                  value={formData.grade || ''}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                >
                  <option value="">Select...</option>
                  <option value="Pre-K">Pre-K</option>
                  <option value="Kindergarten">Kindergarten</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}th Grade
                    </option>
                  ))}
                </select>
              </div>

              <div
                className="form-group"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={formData.hasAttendanceConcerns || false}
                    onChange={(e) =>
                      setFormData({ ...formData, hasAttendanceConcerns: e.target.checked })
                    }
                  />{' '}
                  Has Attendance Concerns
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.hasDisability || false}
                    onChange={(e) => setFormData({ ...formData, hasDisability: e.target.checked })}
                  />{' '}
                  Has Documented Disability
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/clients')}>
            Cancel
          </button>
          <button type="submit" className="button-primary" disabled={loading}>
            {loading ? 'Creating Client...' : 'Create Client'}
          </button>
        </div>
      </form>
    </div>
  );
}
